import { useMemo, useState, useEffect, useRef } from "react";
import { getDemografia } from "services/Reports";
import { Table, Input, Button, Modal } from "antd";
import { FaFilePdf } from "react-icons/fa";
import ExportExcel from "components/excel/ExportExcel";
import Loader from "components/loader";
import Card from "components/card";
import DonutChart from "components/charts/DonutChart";
import Chart from "react-apexcharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import dayjs from "dayjs";
import PickApplyLogo from "assets/img/admin.png";
import {
  generateColor,
  countByBucket,
  ANTIGUEDAD_BUCKETS,
  ACTUALIZACION_BUCKETS,
  getTimeBucket,
  getSearchColumnProps,
  toSafeFileName,
} from "utils/reportHelpers";

const PROVINCIA_DISPLAY_LIMIT = 10;
const PROVINCIA_PDF_DISPLAY_LIMIT = 4;

const DEFAULT_COLUMN_FILTERS = {
  email: [],
  nombre: [],
  apellido: [],
  localidad: [],
  Genero: [],
  provincia: [],
  edad: [],
  creado: [],
  actualizado: [],
};

// Rangos fijos para edad (en años) — específico de este reporte, el resto de los
// buckets (antigüedad/actualización) son compartidos entre reportes.
const EDAD_BUCKETS = [
  { value: "18-24", text: "18 a 24", min: 18, max: 24 },
  { value: "25-34", text: "25 a 34", min: 25, max: 34 },
  { value: "35-44", text: "35 a 44", min: 35, max: 44 },
  { value: "45-54", text: "45 a 54", min: 45, max: 54 },
  { value: "55-64", text: "55 a 64", min: 55, max: 64 },
  { value: "65+", text: "65 o más", min: 65, max: Infinity },
];

const getEdadBucket = (edad) => {
  if (edad == null) return null;
  return EDAD_BUCKETS.find((b) => edad >= b.min && edad <= b.max)?.value || null;
};

const Demography = () => {
  const [allData, setAllData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [columnFilters, setColumnFilters] = useState(DEFAULT_COLUMN_FILTERS);
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [reportName, setReportName] = useState("Reporte de demografía");
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const pdfRef = useRef(null);

  const fetchDemografia = async () => {
    try {
      setIsLoading(true);
      const data = await getDemografia();
      setIsLoading(false);

      if (data) {
        setAllData(data[0] || []);
      }
    } catch (error) {
      setIsLoading(false);
      return error;
    }
  };

  useEffect(() => {
    fetchDemografia();
  }, []);

  const generoOptions = useMemo(() => {
    const values = new Set(allData.map((item) => item.Genero).filter(Boolean));
    return Array.from(values).sort().map((v) => ({ text: v, value: v }));
  }, [allData]);

  const provinciaOptions = useMemo(() => {
    const values = new Set(allData.map((item) => item.provincia).filter(Boolean));
    return Array.from(values).sort().map((v) => ({ text: v, value: v }));
  }, [allData]);

  const filteredData = useMemo(() => {
    return allData.filter((entry) => {
      if (columnFilters.email.length > 0 && !entry.email?.toLowerCase().includes(columnFilters.email[0].toLowerCase())) return false;
      if (columnFilters.nombre.length > 0 && !entry.nombre?.toLowerCase().includes(columnFilters.nombre[0].toLowerCase())) return false;
      if (columnFilters.apellido.length > 0 && !entry.apellido?.toLowerCase().includes(columnFilters.apellido[0].toLowerCase())) return false;
      if (columnFilters.localidad.length > 0 && !entry.localidad?.toLowerCase().includes(columnFilters.localidad[0].toLowerCase())) return false;
      if (columnFilters.Genero.length > 0 && !columnFilters.Genero.includes(entry.Genero)) return false;
      if (columnFilters.provincia.length > 0 && !columnFilters.provincia.includes(entry.provincia)) return false;

      if (columnFilters.edad.length > 0) {
        const bucket = getEdadBucket(entry.Edad);
        if (!bucket || !columnFilters.edad.includes(bucket)) return false;
      }

      if (columnFilters.creado.length > 0) {
        const bucket = getTimeBucket(entry.creado, ANTIGUEDAD_BUCKETS, "month");
        if (!bucket || !columnFilters.creado.includes(bucket)) return false;
      }

      if (columnFilters.actualizado.length > 0) {
        const bucket = getTimeBucket(entry.actualizado, ACTUALIZACION_BUCKETS, "day");
        if (!bucket || !columnFilters.actualizado.includes(bucket)) return false;
      }

      return true;
    });
  }, [allData, columnFilters]);

  const hasActiveFilters = Object.values(columnFilters).some((arr) => arr.length > 0);

  const buildExcelRows = (data) =>
    data.map((item) => ({
      User_ID: item.user_id,
      Username: item.name,
      Email: item.email,
      Nombre: item.nombre,
      Apellido: item.apellido,
      "Fecha de nacimiento": item.fecha_nac ? dayjs(item.fecha_nac).format("DD/MM/YYYY") : "",
      Edad: item.Edad,
      Género: item.Genero,
      Ciudad: item.localidad,
      Provincia: item.provincia,
      "Antiguedad en la plataforma": item.creado ? dayjs(item.creado).format("DD/MM/YYYY") : "",
      "Última actualización": item.actualizado ? dayjs(item.actualizado).format("DD/MM/YYYY") : "",
    }));

  const dataExcelFiltered = useMemo(() => buildExcelRows(filteredData), [filteredData]);
  const dataExcelAll = useMemo(() => buildExcelRows(allData), [allData]);

  // Todos los gráficos se calculan sobre filteredData: el 100% siempre es la
  // cantidad de postulantes que dice el contador de arriba, así que cambian solos
  // a medida que se aplican filtros.
  const totalFiltrado = filteredData.length;

  const edadDistribution = useMemo(
    () => countByBucket(filteredData, (item) => getEdadBucket(item.Edad), EDAD_BUCKETS),
    [filteredData]
  );

  const antiguedadDistribution = useMemo(
    () => countByBucket(filteredData, (item) => getTimeBucket(item.creado, ANTIGUEDAD_BUCKETS, "month"), ANTIGUEDAD_BUCKETS),
    [filteredData]
  );

  const actualizacionDistribution = useMemo(
    () => countByBucket(filteredData, (item) => getTimeBucket(item.actualizado, ACTUALIZACION_BUCKETS, "day"), ACTUALIZACION_BUCKETS),
    [filteredData]
  );

  const generoDistribution = useMemo(() => {
    const counts = new Map();
    filteredData.forEach((item) => {
      const label = item.Genero || "Sin dato";
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);
  }, [filteredData]);

  const provinciaDistribution = useMemo(() => {
    const counts = new Map();
    filteredData.forEach((item) => {
      const label = item.provincia || "Sin dato";
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);
  }, [filteredData]);

  const maxProvinciaCount = provinciaDistribution[0]?.count || 1;
  const provinciaDistributionTop = provinciaDistribution.slice(0, PROVINCIA_DISPLAY_LIMIT);
  const provinciaExtraCount = provinciaDistribution.length - provinciaDistributionTop.length;
  // En el PDF el límite es más chico que en pantalla, para asegurarnos que todo
  // entre en una sola hoja A4.
  const provinciaDistributionPdfTop = provinciaDistribution.slice(0, PROVINCIA_PDF_DISPLAY_LIMIT);
  const provinciaPdfExtraCount = provinciaDistribution.length - provinciaDistributionPdfTop.length;

  const edadChartOptions = useMemo(
    () => ({
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true } },
      xaxis: { categories: edadDistribution.map((d) => d.label) },
      colors: edadDistribution.map((_, i) => generateColor(i, edadDistribution.length)),
      legend: { show: false },
      dataLabels: { enabled: true },
      tooltip: { theme: "dark" },
    }),
    [edadDistribution]
  );

  const antiguedadChartOptions = useMemo(
    () => ({
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true } },
      xaxis: { categories: antiguedadDistribution.map((d) => d.label) },
      colors: antiguedadDistribution.map((_, i) => generateColor(i, antiguedadDistribution.length)),
      legend: { show: false },
      dataLabels: { enabled: true },
      tooltip: { theme: "dark" },
    }),
    [antiguedadDistribution]
  );

  const actualizacionChartOptions = useMemo(
    () => ({
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true } },
      xaxis: { categories: actualizacionDistribution.map((d) => d.label) },
      colors: actualizacionDistribution.map((_, i) => generateColor(i, actualizacionDistribution.length)),
      legend: { show: false },
      dataLabels: { enabled: true },
      tooltip: { theme: "dark" },
    }),
    [actualizacionDistribution]
  );

  const generoChartOptions = useMemo(
    () => ({
      chart: { type: "donut" },
      labels: generoDistribution.map((d) => d.label),
      colors: generoDistribution.map((_, i) => generateColor(i, generoDistribution.length)),
      legend: { show: true, position: "bottom" },
      dataLabels: { enabled: true },
      tooltip: { theme: "dark" },
    }),
    [generoDistribution]
  );

  // Captura la vista oculta armada para el PDF y la parte en tantas páginas A4 como
  // haga falta (a diferencia del reporte de Postulaciones, acá no hay una tabla
  // grande que necesite arrancar en hoja aparte, así que alcanza con una sola
  // captura que se corta sola por altura).
  const handleDownloadPdf = async () => {
    if (!pdfRef.current) return;

    setIsGeneratingPdf(true);
    try {
      const canvas = await html2canvas(pdfRef.current, {
        useCORS: true,
        allowTaint: true,
        scale: 2,
        backgroundColor: "#ffffff",
      });

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const marginMm = 6;
      const pageWidthMm = pdf.internal.pageSize.getWidth();
      const pageHeightMm = pdf.internal.pageSize.getHeight();
      const usableWidthMm = pageWidthMm - marginMm * 2;
      const usableHeightMm = pageHeightMm - marginMm * 2;

      const imgData = canvas.toDataURL("image/png");
      const imgHeightMm = (canvas.height * usableWidthMm) / canvas.width;

      let heightLeftMm = imgHeightMm;
      let renderedHeightMm = 0;

      pdf.addImage(imgData, "PNG", marginMm, marginMm, usableWidthMm, imgHeightMm);
      heightLeftMm -= usableHeightMm;

      while (heightLeftMm > 0) {
        renderedHeightMm += usableHeightMm;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", marginMm, marginMm - renderedHeightMm, usableWidthMm, imgHeightMm);
        heightLeftMm -= usableHeightMm;
      }

      const safeName = toSafeFileName(reportName, "Reporte de demografía");
      pdf.save(`${safeName}_${dayjs().format("YYYYMMDD_HHmm")}.pdf`);
      setIsNameModalOpen(false);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const columns = [
    { title: "User_ID", dataIndex: "user_id" },
    { title: "Username", dataIndex: "name" },
    {
      title: "Email",
      dataIndex: "email",
      ...getSearchColumnProps(columnFilters.email, "Buscar email"),
    },
    {
      title: "Nombre",
      dataIndex: "nombre",
      ...getSearchColumnProps(columnFilters.nombre, "Buscar nombre"),
    },
    {
      title: "Apellido",
      dataIndex: "apellido",
      ...getSearchColumnProps(columnFilters.apellido, "Buscar apellido"),
    },
    {
      title: "Fecha de nacimiento",
      dataIndex: "fecha_nac",
      sorter: (a, b) => dayjs(a.fecha_nac).unix() - dayjs(b.fecha_nac).unix(),
      render: (text) => (text ? dayjs(text).format("DD/MM/YYYY") : "—"),
    },
    {
      title: "Edad",
      dataIndex: "Edad",
      sorter: (a, b) => a.Edad - b.Edad,
      filters: EDAD_BUCKETS.map(({ value, text }) => ({ value, text })),
      filteredValue: columnFilters.edad,
    },
    {
      title: "Género",
      dataIndex: "Genero",
      filters: generoOptions,
      filteredValue: columnFilters.Genero,
    },
    {
      title: "Ciudad",
      dataIndex: "localidad",
      ...getSearchColumnProps(columnFilters.localidad, "Buscar ciudad"),
    },
    {
      title: "Provincia",
      dataIndex: "provincia",
      filters: provinciaOptions,
      filteredValue: columnFilters.provincia,
      filterSearch: true,
    },
    {
      title: "Antiguedad en la plataforma",
      dataIndex: "creado",
      sorter: (a, b) => dayjs(a.creado).unix() - dayjs(b.creado).unix(),
      render: (text) => (text ? dayjs(text).format("DD/MM/YYYY") : "—"),
      filters: ANTIGUEDAD_BUCKETS.map(({ value, text }) => ({ value, text })),
      filteredValue: columnFilters.creado,
    },
    {
      title: "Última actualización",
      dataIndex: "actualizado",
      sorter: (a, b) => dayjs(a.actualizado).unix() - dayjs(b.actualizado).unix(),
      render: (text) => (text ? dayjs(text).format("DD/MM/YYYY") : "—"),
      filters: ACTUALIZACION_BUCKETS.map(({ value, text }) => ({ value, text })),
      filteredValue: columnFilters.actualizado,
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <h1 className="text-3xl font-medium">/ Demografía</h1>

      <div className="flex flex-row items-center justify-between">
        <span className="text-sm font-semibold">{filteredData.length} postulantes encontrados</span>
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <ExportExcel data={dataExcelFiltered} fileName={"Demografia_resultados_" + new Date().toLocaleDateString()} title="Exportar resultados" />
          )}
          <ExportExcel data={dataExcelAll} fileName={"Demografia_" + new Date().toLocaleDateString()} title="Exportar todo" />
          {totalFiltrado > 0 && (
            <Button
              icon={<FaFilePdf />}
              onClick={() => setIsNameModalOpen(true)}
              className="flex items-center gap-2 !bg-red-500 !text-white hover:!bg-red-600"
            >
              Descargar PDF
            </Button>
          )}
        </div>
      </div>

      <Modal
        title="Nombre del reporte"
        open={isNameModalOpen}
        onOk={handleDownloadPdf}
        onCancel={() => setIsNameModalOpen(false)}
        okText="Generar PDF"
        cancelText="Cancelar"
        confirmLoading={isGeneratingPdf}
      >
        <p className="mb-2 text-sm text-gray-500">
          Como acá se puede filtrar de muchas formas distintas, ponele un nombre que identifique qué es este reporte.
        </p>
        <Input
          value={reportName}
          onChange={(e) => setReportName(e.target.value)}
          placeholder="Ej: Postulantes de Buenos Aires, 25 a 34 años"
        />
      </Modal>

      {isLoading ? (
        <Loader />
      ) : (
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey={(record) => record.user_id}
          size="small"
          locale={{ emptyText: "No hay resultados" }}
          onChange={(pagination, tableFilters, sorter, extra) => {
            if (extra.action === "filter") {
              setColumnFilters({
                email: tableFilters.email || [],
                nombre: tableFilters.nombre || [],
                apellido: tableFilters.apellido || [],
                localidad: tableFilters.localidad || [],
                Genero: tableFilters.Genero || [],
                provincia: tableFilters.provincia || [],
                edad: tableFilters.Edad || [],
                creado: tableFilters.creado || [],
                actualizado: tableFilters.actualizado || [],
              });
            }
          }}
        />
      )}

      {!isLoading && (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 items-start">
            <Card extra="w-full h-[380px] px-6 py-4">
              <div className="text-xl font-bold text-navy-700 dark:text-white">Rango de edad</div>
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">Sobre {totalFiltrado} postulantes</p>
              {totalFiltrado > 0 ? (
                <div className="h-[300px] w-full">
                  <Chart
                    options={edadChartOptions}
                    series={[{ name: "Postulantes", data: edadDistribution.map((d) => d.count) }]}
                    type="bar"
                    width="100%"
                    height="100%"
                  />
                </div>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">No hay datos para el filtro actual</p>
              )}
            </Card>

            <Card extra="w-full h-[380px] px-6 py-4">
              <div className="text-xl font-bold text-navy-700 dark:text-white">Género</div>
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">Sobre {totalFiltrado} postulantes</p>
              {totalFiltrado > 0 ? (
                <div className="flex h-[300px] w-full items-center justify-center">
                  <DonutChart options={generoChartOptions} series={generoDistribution.map((d) => d.count)} />
                </div>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">No hay datos para el filtro actual</p>
              )}
            </Card>
          </div>

          <Card extra="w-full px-6 py-4">
            <div className="text-xl font-bold text-navy-700 dark:text-white">Provincia</div>
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">Sobre {totalFiltrado} postulantes</p>
            {provinciaDistributionTop.length > 0 ? (
              <div className="flex flex-col gap-2">
                {provinciaDistributionTop.map((row, idx) => {
                  const pct = totalFiltrado > 0 ? Math.round((row.count / totalFiltrado) * 1000) / 10 : 0;
                  const color = generateColor(idx, provinciaDistributionTop.length);

                  return (
                    <div key={row.label} className="flex items-center gap-3">
                      <span className="w-40 shrink-0 truncate text-sm font-medium text-navy-700 dark:text-white" title={row.label}>
                        {row.label}
                      </span>
                      <div className="h-3 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-navy-700">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${(row.count / maxProvinciaCount) * 100}%`, backgroundColor: color }}
                        />
                      </div>
                      <span className="w-24 shrink-0 text-right text-xs text-gray-500 dark:text-gray-400">
                        {row.count} ({pct}%)
                      </span>
                    </div>
                  );
                })}
                {provinciaExtraCount > 0 && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">+ {provinciaExtraCount} provincias más</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-400">No hay datos para el filtro actual</p>
            )}
          </Card>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 items-start">
            <Card extra="w-full h-[420px] px-6 py-4">
              <div className="text-xl font-bold text-navy-700 dark:text-white">Antigüedad en la plataforma</div>
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">Sobre {totalFiltrado} postulantes</p>
              {totalFiltrado > 0 ? (
                <div className="h-[340px] w-full">
                  <Chart
                    options={antiguedadChartOptions}
                    series={[{ name: "Postulantes", data: antiguedadDistribution.map((d) => d.count) }]}
                    type="bar"
                    width="100%"
                    height="100%"
                  />
                </div>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">No hay datos para el filtro actual</p>
              )}
            </Card>

            <Card extra="w-full h-[420px] px-6 py-4">
              <div className="text-xl font-bold text-navy-700 dark:text-white">Última actualización</div>
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">Sobre {totalFiltrado} postulantes</p>
              {totalFiltrado > 0 ? (
                <div className="h-[340px] w-full">
                  <Chart
                    options={actualizacionChartOptions}
                    series={[{ name: "Postulantes", data: actualizacionDistribution.map((d) => d.count) }]}
                    type="bar"
                    width="100%"
                    height="100%"
                  />
                </div>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">No hay datos para el filtro actual</p>
              )}
            </Card>
          </div>
        </div>
      )}

      {/* Layout exclusivo para el PDF: mismo criterio que el reporte de Postulaciones
          (angosto tipo A4, apilado, membrete con logo). Se renderiza siempre fuera
          de pantalla para que los gráficos ya estén dibujados al generar el PDF. */}
      <div
        ref={pdfRef}
        aria-hidden="true"
        className="pointer-events-none fixed -left-[9999px] top-0 flex w-[820px] flex-col gap-5 bg-white p-5"
      >
        <div className="flex items-center justify-between border-b-4 border-main pb-3">
          <img src={PickApplyLogo} alt="PickApply" className="h-16" />
          <span className="text-sm font-semibold text-gray-500">Reporte de demografía</span>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[32px] font-extrabold text-navy-700">{reportName || "Reporte de demografía"}</span>
          <span className="mt-1 text-base text-gray-500">
            {totalFiltrado} postulantes · Generado el {dayjs().format("DD/MM/YYYY HH:mm")}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-5">
            <span className="text-xl font-bold text-navy-700">Rango de edad</span>
            {totalFiltrado > 0 ? (
              <div className="h-[260px] w-full">
                <Chart
                  options={edadChartOptions}
                  series={[{ name: "Postulantes", data: edadDistribution.map((d) => d.count) }]}
                  type="bar"
                  width="100%"
                  height="100%"
                />
              </div>
            ) : (
              <p className="text-base text-gray-500">Sin datos</p>
            )}
          </div>

          <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-5">
            <span className="text-xl font-bold text-navy-700">Género</span>
            {totalFiltrado > 0 ? (
              <div className="flex h-[260px] w-full items-center justify-center">
                <DonutChart options={generoChartOptions} series={generoDistribution.map((d) => d.count)} />
              </div>
            ) : (
              <p className="text-base text-gray-500">Sin datos</p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-5">
          <span className="text-xl font-bold text-navy-700">Provincia</span>
          {provinciaDistributionPdfTop.length > 0 ? (
            <div className="mt-1 flex flex-col gap-2">
              {provinciaDistributionPdfTop.map((row, idx) => {
                const pct = totalFiltrado > 0 ? Math.round((row.count / totalFiltrado) * 1000) / 10 : 0;
                const color = generateColor(idx, provinciaDistributionPdfTop.length);

                return (
                  <div key={row.label} className="flex items-center gap-3">
                    <span className="w-40 shrink-0 truncate text-base font-medium text-navy-700">{row.label}</span>
                    <div className="h-3 flex-1 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${(row.count / maxProvinciaCount) * 100}%`, backgroundColor: color }}
                      />
                    </div>
                    <span className="w-28 shrink-0 text-right text-sm text-gray-500">
                      {row.count} ({pct}%)
                    </span>
                  </div>
                );
              })}
              {provinciaPdfExtraCount > 0 && (
                <p className="mt-1 text-sm text-gray-500">+ {provinciaPdfExtraCount} provincias más</p>
              )}
            </div>
          ) : (
            <p className="text-base text-gray-500">Sin datos</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-5">
            <span className="text-xl font-bold text-navy-700">Antigüedad en la plataforma</span>
            {totalFiltrado > 0 ? (
              <div className="h-[280px] w-full">
                <Chart
                  options={antiguedadChartOptions}
                  series={[{ name: "Postulantes", data: antiguedadDistribution.map((d) => d.count) }]}
                  type="bar"
                  width="100%"
                  height="100%"
                />
              </div>
            ) : (
              <p className="text-base text-gray-500">Sin datos</p>
            )}
          </div>

          <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-5">
            <span className="text-xl font-bold text-navy-700">Última actualización</span>
            {totalFiltrado > 0 ? (
              <div className="h-[280px] w-full">
                <Chart
                  options={actualizacionChartOptions}
                  series={[{ name: "Postulantes", data: actualizacionDistribution.map((d) => d.count) }]}
                  type="bar"
                  width="100%"
                  height="100%"
                />
              </div>
            ) : (
              <p className="text-base text-gray-500">Sin datos</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Demography;
